import { clsx } from 'clsx';
import {
  forwardRef,
  memo,
  useMemo,
  useSyncExternalStore,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import { DetailsLayout } from './details-layout/DetailsLayout.js';
import type { ExtractedContentSlot as DetailsContentSlot } from './details-layout/slots.js';
import * as styles from './PageLayout.sty.js';
import {
  PageLayoutPortalContext,
  usePageLayoutPortalContext,
} from './shared/PageLayoutPortalContext.js';
import {
  PageLayoutContent,
  PageLayoutDetailsWithControlBar,
  PageLayoutHeader,
  PageLayoutSidebar,
} from './shared/portal-slots.js';
import type {
  DetailsSlotConfig,
  SidebarSlotConfig,
} from './shared/slot-config.js';
import { contentDefaults } from './shared/slot-defaults.js';
import { createSlotRegistry } from './shared/slot-registry.js';
import { TeleportTarget } from './shared/TeleportTarget.js';
import { useStableHost } from './shared/useStableHost.js';
import { SidebarLayout } from './sidebar-layout/SidebarLayout.js';
import type { ExtractedContentSlot as SidebarContentSlot } from './sidebar-layout/slots.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';

function resolveOptionalSlot<T extends SidebarSlotConfig | DetailsSlotConfig>(
  config: T | null,
  target: ReactNode,
): (T & { children: ReactNode }) | null {
  if (!config) {
    return null;
  }
  return { ...config, children: target };
}

/**
 * This is the container for the horizontal layout below the Header.
 * Subscribes to the slot registry via `useSyncExternalStore`.
 * Re-renders only when registry state changes.
 */
function MainShell() {
  const { hosts, registry } = usePageLayoutPortalContext();
  const snapshot = useSyncExternalStore(
    registry.subscribe,
    registry.getSnapshot,
  );

  const sidebarConfig = snapshot.sidebar;
  const detailsConfig = snapshot.details;
  const detailsLayoutContext = snapshot.detailsLayoutContext;

  const showTopLeftRadius = sidebarConfig !== null;
  const showTopRightRadius =
    detailsConfig !== null && detailsLayoutContext?.mode !== 'collapsed';

  const contentClassName = clsx(
    styles.content,
    showTopLeftRadius && styles.contentTopLeftRadius,
    showTopRightRadius && styles.contentTopRightRadius,
  );

  const sidebarTarget = useMemo(
    () => <TeleportTarget host={hosts.sidebar} />,
    [hosts.sidebar],
  );
  const detailsTarget = useMemo(
    () => <TeleportTarget host={hosts.details} />,
    [hosts.details],
  );
  const contentTarget = useMemo(
    () => <TeleportTarget host={hosts.content} />,
    [hosts.content],
  );

  const sidebarSlot = useMemo(
    () => resolveOptionalSlot(snapshot.sidebar, sidebarTarget),
    [snapshot.sidebar, sidebarTarget],
  );
  const detailsSlot = useMemo(
    () => resolveOptionalSlot(snapshot.details, detailsTarget),
    [snapshot.details, detailsTarget],
  );

  const contentSlot = useMemo<DetailsContentSlot>(() => {
    if (snapshot.content) {
      const { config, domProps, ref } = snapshot.content;
      return {
        children: contentTarget,
        config,
        domProps: {
          ...domProps,
          className: clsx(contentClassName, domProps.className),
        },
        ref,
      };
    }
    return {
      children: undefined,
      config: { minWidth: contentDefaults.minWidth },
      domProps: { className: contentClassName },
    };
  }, [snapshot.content, contentTarget, contentClassName]);

  const sidebarContentSlot = useMemo<SidebarContentSlot>(
    () => ({
      children: <DetailsLayout details={detailsSlot} content={contentSlot} />,
      // The sidebar bounds itself via its own maxWidth, so the right-hand region
      // needs no minimum. This is distinct from the details content pane's min.
      config: { minWidth: 0 },
      domProps: {},
    }),
    [detailsSlot, contentSlot],
  );

  return <SidebarLayout sidebar={sidebarSlot} content={sidebarContentSlot} />;
}

/**
 * Subscribes to the slot registry for header config.
 */
function HeaderShell() {
  const { hosts, registry } = usePageLayoutPortalContext();
  const snapshot = useSyncExternalStore(
    registry.subscribe,
    registry.getSnapshot,
  );
  const headerConfig = snapshot.header;

  if (headerConfig === null) {
    return null;
  }

  return (
    <div
      {...headerConfig.domProps}
      ref={headerConfig.ref}
      className={clsx(styles.headerContainer, headerConfig.domProps.className)}
    >
      <TeleportTarget host={hosts.header} />
    </div>
  );
}

/** @public */
export interface PageLayoutProps
  extends PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {}

const LayoutRoot = memo(
  forwardRef<HTMLDivElement, PageLayoutProps>((props, forwardedRef) => {
    const { children, className: rootClassName, ...rootProps } = props;

    const [behavioralTrackingProps, restRootProps] =
      useBehavioralTrackingProps(rootProps);

    const headerHost = useStableHost();
    const sidebarHost = useStableHost();
    const contentHost = useStableHost();
    const detailsHost = useStableHost();

    const registry = useMemo(() => createSlotRegistry(), []);

    const context = useMemo(
      () => ({
        hosts: {
          header: headerHost,
          sidebar: sidebarHost,
          content: contentHost,
          details: detailsHost,
        },
        registry,
      }),
      // hosts and registry are stable
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    return (
      <PageLayoutPortalContext.Provider value={context}>
        <div
          {...restRootProps}
          {...behavioralTrackingProps}
          ref={forwardedRef}
          className={clsx(styles.layoutRoot, rootClassName)}
        >
          <HeaderShell />
          <div className={styles.contentContainer}>
            <MainShell />
            {children}
          </div>
        </div>
      </PageLayoutPortalContext.Provider>
    );
  }),
);
LayoutRoot.displayName = 'PageLayout';

/**
 * Use the `PageLayout` to structure app pages with persistent header, sidebar, content, and details panel slots, including responsive breakpoint handling and resizable panels.
 * @public
 */
export const PageLayout = Object.assign(LayoutRoot, {
  /** Displays content at the very top of the page. */
  Header: PageLayoutHeader,
  /** Displays content in the collapsible, resizable sidebar panel on the left. */
  Sidebar: PageLayoutSidebar,
  /**
   * Displays the styled main content area (padding, background, border-radius).
   */
  Content: PageLayoutContent,
  /** Displays content in the collapsible, resizable details panel on the right. */
  Details: PageLayoutDetailsWithControlBar,
});

export { usePageLayoutPortalContext };
